import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CSpinner } from '@coreui/react';
import api from '../services/api';


const STATUS_LABELS = {
  'Baru': 'Received',
  'Dalam Proses': 'In Progress',
  'Selesai': 'Testing / Ready',
  'Dipulangkan': 'Returned',
  'Menunggu Maklumbalas': 'Awaiting Feedback',
  'Waiting to Pickup': 'Ready for Pickup',
};

const STEP_ICONS = [
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
    <rect x="9" y="2" width="6" height="4" rx="1"/><rect x="3" y="6" width="18" height="16" rx="2"/>
    <line x1="8" y1="12" x2="16" y2="12"/><line x1="8" y1="16" x2="13" y2="16"/>
  </svg>,
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
  </svg>,
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>,
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
  </svg>,
];

const STEP_DESC = [
  'We have received your item.',
  'Our technicians are working on it.',
  'Quality check and testing in progress.',
  'Your item is ready for pickup / delivery.',
];

function getStepIndex(status) {
  const map = {
    'Baru': 0, 'Dalam Proses': 1,
    'Menunggu Maklumbalas': 1,
    'Selesai': 2, 'Waiting to Pickup': 3,
    'Dipulangkan': 3,
  };
  return map[status] ?? 0;
}

// ── Registration Modal ────────────────────────────────────────────────────────
const MODAL_STEP = { INTRO: 0, OTP: 1, CONFIRM: 2, SET_PASSWORD: 3, DONE: 4, ALREADY_REGISTERED: 5 };

function RegisterModal({ prefillPhone, onClose, onSuccess }) {
  const [step, setStep] = useState(MODAL_STEP.INTRO);
  const [phone, setPhone] = useState(prefillPhone || '');
  const [otp, setOtp] = useState('');
  const [customerInfo, setCustomerInfo] = useState(null); // { nama, email, uid }
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [otpCountdown, setOtpCountdown] = useState(0);

  useEffect(() => {
    if (otpCountdown <= 0) return;
    const t = setTimeout(() => setOtpCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [otpCountdown]);

  async function handleSendOtp(e) {
    e && e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/customer/send-otp', { phone: phone.trim(), purpose: 'register' });
      setCustomerInfo(data.customer);
      setEmail(data.customer.email || '');
      setStep(MODAL_STEP.OTP);
      setOtpCountdown(600);
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to send OTP.';
      if (err.response?.status === 409 || msg.includes('already registered')) {
        setStep(MODAL_STEP.ALREADY_REGISTERED);
      } else {
        setError(msg);
      }
    } finally { setLoading(false); }
  }

  async function handleVerifyOtp(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/customer/verify-otp', { phone: phone.trim(), otp: otp.trim(), purpose: 'register' });
      setCustomerInfo(data.customer);
      setEmail(data.customer.email || '');
      setStep(MODAL_STEP.CONFIRM);
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid OTP.');
    } finally { setLoading(false); }
  }

  async function handleRegister(e) {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) { setError('Passwords do not match.'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    setLoading(true);
    try {
      const { data } = await api.post('/customer/register', {
        phone: phone.trim(),
        password,
        email: email.trim() || undefined,
      });
      sessionStorage.setItem('customer_token', data.token);
      sessionStorage.setItem('customer_user', JSON.stringify(data.customer));
      setStep(MODAL_STEP.DONE);
      setTimeout(() => onSuccess(data), 1500);
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed.');
    } finally { setLoading(false); }
  }

  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: 16,
  };
  const box = {
    background: '#fff', borderRadius: 14, width: '100%', maxWidth: 420,
    boxShadow: '0 8px 40px rgba(0,0,0,0.18)', overflow: 'hidden',
  };
  const header = {
    background: 'linear-gradient(135deg, #071e2b, #0d3a4a)',
    padding: '20px 24px',
  };
  const body = { padding: '24px' };
  const inp = {
    width: '100%', padding: '10px 12px', border: '1px solid #dde1e7',
    borderRadius: 8, fontSize: 14, boxSizing: 'border-box', outline: 'none',
    marginBottom: 12,
  };
  const btn = {
    width: '100%', padding: '11px', background: '#8bc34a', color: '#fff',
    border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14,
    cursor: 'pointer', marginTop: 4,
  };
  const btnDisabled = { ...btn, opacity: 0.6, cursor: 'not-allowed' };

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={box}>
        <div style={header}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ color: '#8bc34a', fontWeight: 700, fontSize: 16, letterSpacing: 0.5 }}>FMZ Points</div>
              <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, marginTop: 2 }}>Register to earn loyalty points</div>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>×</button>
          </div>
          {/* Steps indicator */}
          <div style={{ display: 'flex', gap: 4, marginTop: 14 }}>
            {['Phone', 'OTP', 'Confirm', 'Password'].map((label, i) => (
              <div key={label} style={{ flex: 1, textAlign: 'center' }}>
                <div style={{
                  height: 4, borderRadius: 2,
                  background: step > i ? '#8bc34a' : step === i ? '#8bc34a' : 'rgba(255,255,255,0.2)',
                  opacity: step >= i ? 1 : 0.4,
                  marginBottom: 4,
                }} />
                <div style={{ color: step >= i ? '#8bc34a' : 'rgba(255,255,255,0.4)', fontSize: 10 }}>{label}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={body}>
          {error && (
            <div style={{ background: '#fff3f3', border: '1px solid #f5c6cb', borderRadius: 6, padding: '10px 12px', color: '#721c24', fontSize: 13, marginBottom: 14 }}>
              {error}
            </div>
          )}

          {/* Step 0: Enter phone */}
          {step === MODAL_STEP.INTRO && (
            <form onSubmit={handleSendOtp}>
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <div style={{ fontSize: 40, marginBottom: 8 }}>🎁</div>
                <div style={{ fontWeight: 700, fontSize: 16, color: '#0d3a4a', marginBottom: 6 }}>Earn 100 FMZ Points Today!</div>
                <div style={{ fontSize: 13, color: '#6c757d' }}>Register your account and get 100 welcome points instantly. Redeem points for discounts on future repairs.</div>
              </div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>Phone Number</label>
              <input
                style={inp}
                type="text"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                placeholder="e.g. 0123456789"
                required
                maxLength={25}
              />
              <button type="submit" style={loading ? btnDisabled : btn} disabled={loading}>
                {loading ? <CSpinner size="sm" style={{ color: '#fff' }} /> : 'Send Verification Code'}
              </button>
            </form>
          )}

          {/* Step 1: OTP */}
          {step === MODAL_STEP.OTP && (
            <form onSubmit={handleVerifyOtp}>
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>📱</div>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#0d3a4a', marginBottom: 6 }}>Enter Verification Code</div>
                <div style={{ fontSize: 13, color: '#6c757d' }}>
                  A 6-digit code was sent to <strong>{phone}</strong>.{' '}
                  {otpCountdown > 0 && <span style={{ color: '#8bc34a' }}>Expires in {Math.floor(otpCountdown / 60)}:{String(otpCountdown % 60).padStart(2, '0')}</span>}
                </div>
              </div>
              <input
                style={{ ...inp, textAlign: 'center', fontSize: 24, letterSpacing: 8, fontWeight: 700 }}
                type="text"
                inputMode="numeric"
                value={otp}
                onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                required
              />
              <button type="submit" style={loading || otp.length < 6 ? btnDisabled : btn} disabled={loading || otp.length < 6}>
                {loading ? <CSpinner size="sm" style={{ color: '#fff' }} /> : 'Verify Code'}
              </button>
              <button
                type="button"
                onClick={handleSendOtp}
                disabled={otpCountdown > 540}
                style={{ marginTop: 10, width: '100%', background: 'none', border: 'none', color: '#8bc34a', fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}
              >
                Resend OTP
              </button>
            </form>
          )}

          {/* Step 2: Confirm customer info */}
          {step === MODAL_STEP.CONFIRM && customerInfo && (
            <form onSubmit={e => { e.preventDefault(); setStep(MODAL_STEP.SET_PASSWORD); }}>
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>👤</div>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#0d3a4a', marginBottom: 6 }}>Confirm Your Details</div>
                <div style={{ fontSize: 13, color: '#6c757d' }}>Please review and update your email if needed.</div>
              </div>
              <div style={{ background: '#f8f9fb', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: '#8c9bab', marginBottom: 2 }}>NAME</div>
                <div style={{ fontWeight: 700, color: '#0d3a4a', fontSize: 15 }}>{customerInfo.nama}</div>
                <div style={{ fontSize: 12, color: '#8c9bab', marginTop: 8, marginBottom: 2 }}>PHONE</div>
                <div style={{ fontWeight: 600, color: '#0d3a4a' }}>{customerInfo.phone || phone}</div>
              </div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>Email (optional)</label>
              <input
                style={inp}
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="your@email.com"
              />
              <button type="submit" style={btn}>Continue</button>
            </form>
          )}

          {/* Step 3: Set password */}
          {step === MODAL_STEP.SET_PASSWORD && (
            <form onSubmit={handleRegister}>
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>🔐</div>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#0d3a4a', marginBottom: 6 }}>Set Your Password</div>
                <div style={{ fontSize: 13, color: '#6c757d' }}>Create a password to access your FMZ Points account.</div>
              </div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>Password</label>
              <input style={inp} type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min. 6 characters" required minLength={6} />
              <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>Confirm Password</label>
              <input style={inp} type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Repeat password" required />
              <button type="submit" style={loading ? btnDisabled : btn} disabled={loading}>
                {loading ? <CSpinner size="sm" style={{ color: '#fff' }} /> : 'Create Account & Earn 100 Points'}
              </button>
            </form>
          )}

          {/* Step 4: Done */}
          {step === MODAL_STEP.DONE && (
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <div style={{ fontSize: 56, marginBottom: 12 }}>🎉</div>
              <div style={{ fontWeight: 700, fontSize: 18, color: '#0d3a4a', marginBottom: 8 }}>Welcome to FMZ Points!</div>
              <div style={{ fontSize: 14, color: '#6c757d', marginBottom: 16 }}>Your account is created and <strong style={{ color: '#8bc34a' }}>100 points</strong> have been credited. Redirecting to your dashboard...</div>
              <CSpinner style={{ color: '#8bc34a' }} />
            </div>
          )}

          {/* Step 5: Already registered — prompt login */}
          {step === MODAL_STEP.ALREADY_REGISTERED && (
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>👋</div>
              <div style={{ fontWeight: 700, fontSize: 18, color: '#0d3a4a', marginBottom: 8 }}>You already have an account!</div>
              <div style={{ fontSize: 14, color: '#6c757d', marginBottom: 24 }}>
                <strong>{phone}</strong> is already registered for FMZ Points. Log in to view your points and redeem rewards.
              </div>
              <button
                onClick={() => { onClose(); window.location.href = (process.env.PUBLIC_URL || '') + '/customer/login'; }}
                style={{ width: '100%', padding: '13px', background: '#8bc34a', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 15, cursor: 'pointer', marginBottom: 10 }}
              >
                Login to Customer Portal
              </button>
              <button
                onClick={onClose}
                style={{ width: '100%', padding: '11px', background: 'none', border: '1px solid #dde1e7', borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: 'pointer', color: '#6c757d' }}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function TrackPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [cust, setCust] = useState('');
  const [code, setCode] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showRegister, setShowRegister] = useState(false);

  const runTrack = useCallback(async (tel, jobCode) => {
    const c = String(tel || '').trim();
    const k = String(jobCode || '').trim();
    if (!c || !k) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const { data } = await api.get('/track', { params: { cust: c, code: k } });
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Not found. Please check your phone number and ticket number.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const c = (searchParams.get('cust') || '').trim();
    const k = (searchParams.get('code') || '').trim();
    setCust(c);
    setCode(k);
    if (c && k) {
      runTrack(c, k);
    } else {
      setResult(null);
      setError('');
      setLoading(false);
    }
  }, [searchParams, runTrack]);

  function handleSearch(e) {
    e.preventDefault();
    if (!cust.trim() || !code.trim()) return;
    setSearchParams({ cust: cust.trim(), code: code.trim() }, { replace: true });
  }

  function handleRegisterSuccess(data) {
    setShowRegister(false);
    navigate('/customer/dashboard');
  }

  const activeStep = result ? getStepIndex(result.status) : -1;

  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif", minHeight: '100vh', backgroundColor: '#f4f6f9' }}>

      {showRegister && (
        <RegisterModal
          prefillPhone={cust}
          onClose={() => setShowRegister(false)}
          onSuccess={handleRegisterSuccess}
        />
      )}

      {/* ── HEADER ── */}
      <div style={{
        background: 'linear-gradient(135deg, #071e2b 0%, #0d3a4a 60%, #1a5268 100%)',
        padding: '0',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', inset: 0, opacity: 0.07,
          backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }} />

        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '28px 24px', position: 'relative' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 24 }}>

            {/* Left: logo + tagline */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <img src={`${process.env.PUBLIC_URL}/images/fmzlogo.png`} alt="FMZ Solutions" style={{ height: 56, objectFit: 'contain' }} />
              <div>
                <div style={{ color: '#fff', fontSize: 13, letterSpacing: 2, textTransform: 'uppercase', opacity: 0.7 }}>
                  Smart Solutions. Connected Future.
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button
                    onClick={() => {
                      const params = new URLSearchParams({ ref: 'track' });
                      if (cust.trim()) params.set('cust', cust.trim());
                      if (code.trim()) params.set('code', code.trim());
                      navigate(`/login?${params.toString()}`);
                    }}
                    style={{ padding: '5px 14px', background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                  >
                    Staff Login
                  </button>
                  <button
                    onClick={() => navigate('/customer/login')}
                    style={{ padding: '5px 14px', background: '#8bc34a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                  >
                    My Points
                  </button>
                </div>
              </div>
            </div>

            {/* Right: search */}
            <div style={{ flex: '1 1 340px', maxWidth: 520 }}>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 20, marginBottom: 4 }}>
                TRACK YOUR REPAIR
              </div>
              <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: 13, marginBottom: 12 }}>
                Enter your phone number and ticket / reference number to check status.
              </div>
              <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <div style={{ position: 'relative', flex: '1 1 140px' }}>
                  <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#aaa' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.4 2 2 0 0 1 3.6 1.21h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.77a16 16 0 0 0 6.29 6.29l1.63-1.63a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
                    </svg>
                  </span>
                  <input
                    type="text"
                    placeholder="Phone Number"
                    value={cust}
                    onChange={e => setCust(e.target.value)}
                    required
                    maxLength={25}
                    style={{
                      width: '100%', padding: '10px 10px 10px 30px',
                      borderRadius: 8, border: 'none', fontSize: 13,
                      outline: 'none', boxSizing: 'border-box',
                    }}
                  />
                </div>
                <div style={{ position: 'relative', flex: '1 1 180px' }}>
                  <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#aaa' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                  </span>
                  <input
                    type="text"
                    placeholder="Ticket / Reference No."
                    value={code}
                    onChange={e => setCode(e.target.value)}
                    required
                    maxLength={20}
                    style={{
                      width: '100%', padding: '10px 10px 10px 30px',
                      borderRadius: 8, border: 'none', fontSize: 13,
                      outline: 'none', boxSizing: 'border-box',
                    }}
                  />
                </div>
                <button type="submit" disabled={loading} style={{
                  background: '#8bc34a', color: '#fff', border: 'none',
                  borderRadius: 8, padding: '10px 22px', fontWeight: 700,
                  fontSize: 14, letterSpacing: 1, cursor: 'pointer',
                  whiteSpace: 'nowrap', flexShrink: 0,
                }}>
                  {loading ? <CSpinner size="sm" style={{ color: '#fff' }} /> : 'SEARCH'}
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* ── PROGRESS STEPS ── */}
        <div style={{ background: 'rgba(0,0,0,0.15)', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ maxWidth: 1200, margin: '0 auto', padding: '20px 24px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', gap: 0 }}>
              {['RECEIVED', 'IN PROGRESS', 'TESTING', 'READY'].map((label, i) => {
                const isActive = activeStep === i;
                const isDone = activeStep > i;
                const color = isActive ? '#8bc34a' : isDone ? '#8bc34a' : 'rgba(255,255,255,0.35)';
                return (
                  <React.Fragment key={i}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 120 }}>
                      <div style={{
                        width: 60, height: 60, borderRadius: '50%',
                        border: `2px solid ${color}`,
                        background: isActive ? '#8bc34a' : isDone ? 'rgba(139,195,74,0.2)' : 'rgba(255,255,255,0.06)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: isActive ? '#fff' : color,
                        marginBottom: 8,
                        transition: 'all 0.3s',
                      }}>
                        {STEP_ICONS[i]}
                      </div>
                      <div style={{
                        color: isActive ? '#8bc34a' : isDone ? '#8bc34a' : 'rgba(255,255,255,0.5)',
                        fontWeight: isActive ? 700 : 600,
                        fontSize: 12,
                        letterSpacing: 1,
                        textTransform: 'uppercase',
                        marginBottom: 4,
                      }}>{label}</div>
                      <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11, textAlign: 'center', maxWidth: 110 }}>
                        {STEP_DESC[i]}
                      </div>
                    </div>
                    {i < 3 && (
                      <div style={{
                        flex: 1, height: 2, marginTop: 28,
                        background: activeStep > i ? '#8bc34a' : 'rgba(255,255,255,0.15)',
                        minWidth: 20,
                      }} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* ── FEATURES ROW ── */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e8ecf0' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '16px 24px' }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
            {[
              { icon: '🔔', title: 'REAL-TIME UPDATES', desc: 'Get notified at every step.' },
              { icon: '🛡️', title: 'SECURE & RELIABLE', desc: 'Your device is in safe hands.' },
              { icon: '⚡', title: 'FASTER SERVICE', desc: 'Quick turnaround with quality care.' },
              { icon: '🎧', title: 'SUPPORT', desc: 'Need help? Our team is here for you.' },
            ].map(f => (
              <div key={f.title} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flex: '1 1 200px', padding: '8px 16px' }}>
                <span style={{ fontSize: 22, lineHeight: 1 }}>{f.icon}</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 11, letterSpacing: 1, color: '#0d3a4a' }}>{f.title}</div>
                  <div style={{ fontSize: 12, color: '#6c757d', marginTop: 2 }}>{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── MAIN CONTENT ── */}
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '36px 24px' }}>

        {error && (
          <div style={{
            background: '#fff3f3', border: '1px solid #f5c6cb', borderRadius: 10,
            padding: '16px 20px', color: '#721c24', marginBottom: 24,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            {error}
          </div>
        )}

        {!result && !loading && !error && (
          <div style={{ textAlign: 'center', padding: '48px 0' }}>
            <div style={{ fontSize: 64, marginBottom: 16 }}>🔍</div>
            <div style={{ color: '#0d3a4a', fontWeight: 700, fontSize: 20, marginBottom: 8 }}>Track Your Repair Status</div>
            <div style={{ color: '#6c757d', fontSize: 14, marginBottom: 24 }}>
              Enter your phone number and ticket / reference number above to get real-time updates on your repair.
            </div>
            {/* FMZ Points promo banner */}
            <div style={{
              background: 'linear-gradient(135deg, #071e2b, #0d3a4a)',
              borderRadius: 12, padding: '20px 24px', maxWidth: 460, margin: '0 auto',
              display: 'flex', alignItems: 'center', gap: 16, textAlign: 'left',
            }}>
              <div style={{ fontSize: 40, flexShrink: 0 }}>⭐</div>
              <div>
                <div style={{ color: '#8bc34a', fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Earn FMZ Points</div>
                <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 13, marginBottom: 10 }}>
                  Register your account to earn 100 welcome points and get discounts on every repair.
                </div>
                <button
                  onClick={() => setShowRegister(true)}
                  style={{ padding: '7px 18px', background: '#8bc34a', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
                >
                  Register & Earn 100 Points
                </button>
              </div>
            </div>
          </div>
        )}

        {result && (
          <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 2px 16px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
            {/* Result header */}
            <div style={{
              background: 'linear-gradient(135deg, #071e2b, #0d3a4a)',
              padding: '24px 28px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16,
            }}>
              <div>
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>
                  {result.type === 'warranty' ? 'Warranty Claim' : 'Repair Job'}
                </div>
                <div style={{ color: '#fff', fontSize: 24, fontWeight: 700 }}>#{result.jobcode}</div>
                <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, marginTop: 4 }}>
                  {result.customer?.nama}
                </div>
              </div>
              <div style={{
                background: '#8bc34a', borderRadius: 10,
                padding: '10px 20px', textAlign: 'center',
              }}>
                <div style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>
                  {STATUS_LABELS[result.status] || result.status}
                </div>
                <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 11, marginTop: 2 }}>Current Status</div>
              </div>
            </div>

            {/* Details grid */}
            <div style={{ padding: '24px 28px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                {[
                  { label: 'Date Received', value: result.date },
                  { label: 'Equipment', value: result.jenis || '—' },
                  { label: 'Devices', value: result.alat?.length ? result.alat.join(', ') : '—' },
                  result.no_siri && { label: 'Serial No.', value: result.no_siri },
                  result.staff && { label: 'Assigned Technician', value: result.staff },
                  { label: 'Contact Method', value: result.perhubungan?.join(', ') || '—' },
                ].filter(Boolean).map(item => (
                  <div key={item.label} style={{
                    background: '#f8f9fb', borderRadius: 8, padding: '14px 16px',
                    borderLeft: '3px solid #8bc34a',
                  }}>
                    <div style={{ color: '#8c9bab', fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>
                      {item.label}
                    </div>
                    <div style={{ color: '#0d3a4a', fontWeight: 600, fontSize: 14 }}>{item.value}</div>
                  </div>
                ))}
              </div>

              {result.catatan && (
                <div style={{
                  marginTop: 16, background: '#fffbf0', borderRadius: 8,
                  padding: '14px 16px', border: '1px solid #ffe69c',
                }}>
                  <div style={{ color: '#8c9bab', fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>
                    Notes
                  </div>
                  <div style={{ color: '#5a4a00', fontSize: 14 }}>{result.catatan}</div>
                </div>
              )}

              <div style={{
                marginTop: 20, padding: '14px 16px',
                background: '#f0f9e8', borderRadius: 8,
                border: '1px solid #c5e39a',
                display: 'flex', alignItems: 'center', gap: 10,
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#5a8a2e" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.4 2 2 0 0 1 3.6 1.21h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.77a16 16 0 0 0 6.29 6.29l1.63-1.63a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                <span style={{ color: '#3a6b1a', fontSize: 13 }}>
                  We will notify you via SMS or email when your repair status is updated.
                </span>
              </div>

              {/* FMZ Points CTA — shown after successful track */}
              <div style={{
                marginTop: 16, padding: '16px 20px',
                background: 'linear-gradient(135deg, #071e2b, #0d3a4a)',
                borderRadius: 10,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 28 }}>⭐</span>
                  <div>
                    <div style={{ color: '#8bc34a', fontWeight: 700, fontSize: 14 }}>Earn FMZ Points on this repair!</div>
                    <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, marginTop: 2 }}>Register for free and earn up to 100 points redeemable for discounts.</div>
                  </div>
                </div>
                <button
                  onClick={() => setShowRegister(true)}
                  style={{ padding: '8px 18px', background: '#8bc34a', color: '#fff', border: 'none', borderRadius: 7, fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}
                >
                  Redeem 100 FMZ Points
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── BOTTOM FEATURES ── */}
      <div style={{ background: '#fff', borderTop: '1px solid #e8ecf0', marginTop: 8 }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '20px 24px' }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
            {[
              { icon: '📅', title: 'EASY TRACKING', desc: 'Simple, fast and convenient.' },
              { icon: '💬', title: 'INSTANT NOTIFICATIONS', desc: 'Receive updates via email and SMS.' },
              { icon: '🔒', title: 'DATA PRIVACY', desc: 'Your information is always protected.' },
              { icon: '👥', title: 'CUSTOMER FOCUSED', desc: "We're committed to your satisfaction." },
            ].map(f => (
              <div key={f.title} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flex: '1 1 200px', padding: '8px 16px' }}>
                <span style={{ fontSize: 22, lineHeight: 1 }}>{f.icon}</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 11, letterSpacing: 1, color: '#0d3a4a' }}>{f.title}</div>
                  <div style={{ fontSize: 12, color: '#6c757d', marginTop: 2 }}>{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── FOOTER ── */}
      <div style={{ background: '#071e2b', padding: '16px 24px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 32, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
              www.fmzsolutions.my
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
              sales@fmzsolutions.my
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.4 2 2 0 0 1 3.6 1.21h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.77a16 16 0 0 0 6.29 6.29l1.63-1.63a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
              011-1090 1303
            </div>
          </div>
          <div style={{
            background: '#8bc34a', color: '#fff',
            fontWeight: 700, fontSize: 12, letterSpacing: 1,
            padding: '6px 14px', borderRadius: 4,
          }}>
            SMART SOLUTIONS. CONNECTED FUTURE.
          </div>
        </div>
      </div>
    </div>
  );
}
